import React, { useEffect, useMemo, useState } from "react";
import { X, BarChart3, Clock, Loader2, Server, FileClock, Zap, AlertTriangle, ChevronDown } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { getTrafficStats, getTrafficErrors } from "@/lib/traffic";

const INK = "#141413", IND = "#5B5BD6", HAIR = "#E8E8E5", G9 = "#9A9A96", G6 = "#6C6C69", PANEL = "#F6F6F4";
const LINE_COLORS = ["#5B5BD6", "#1E7F4F", "#D97706", "#DC2626", "#0891B2", "#7C3AED", "#BE185D", "#2563EB"];

const LABELS = {
  home: "Home", patients: "Patients", telehealth: "Telehealth", "encounter-review": "Encounter Review",
  dictation: "Dictation", "chart-documents": "Chart Docs", "clinic-documents": "Clinic Docs",
  "claims-queue": "Claims Queue", "payment-posting": "Payments", "lumina-documents": "Lumina Docs",
  "lumina-at-work": "Agent Activity", unknown: "Other",
};
const label = (m) => LABELS[m] || m;
const WINS = [["today", "Today"], ["week", "Last 7 days"], ["month", "Last 30 days"]];
const fmtMs = (ms) => (ms == null ? "—" : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${ms}ms`);
const fmtDur = (ms) => { if (!ms) return "0s"; const s = ms / 1000; return s >= 3600 ? `${(s / 3600).toFixed(1)}h` : s >= 60 ? `${(s / 60).toFixed(1)}m` : `${s.toFixed(1)}s`; };
const fmtDay = (d) => { const p = (d || "").split("-"); return p.length === 3 ? `${p[1]}/${p[2]}` : d; };
const fmtTs = (iso) => { try { const d = new Date(iso); return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso || ""; } };
const statusLabel = (s) => (s === 0 ? "No response" : String(s));

export function UsageStatsPanel({ open, onClose, email }) {
  const [win, setWin] = useState("today");
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [errOpen, setErrOpen] = useState(false);
  const [errData, setErrData] = useState(null);
  const [errStatus, setErrStatus] = useState("idle");

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setStatus("loading");
    getTrafficStats({ email, days: 30 })
      .then((d) => { if (alive) { setData(d); setStatus("done"); } })
      .catch(() => { if (alive) setStatus("error"); });
    return () => { alive = false; };
  }, [open, email]);

  useEffect(() => { setErrOpen(false); setErrData(null); setErrStatus("idle"); }, [open, email]);

  const toggleErrors = () => {
    const next = !errOpen;
    setErrOpen(next);
    if (next && errStatus === "idle") {
      setErrStatus("loading");
      getTrafficErrors({ email, days: 30, limit: 50 })
        .then((d) => { setErrData(d); setErrStatus("done"); })
        .catch(() => setErrStatus("error"));
    }
  };

  const rows = (data && data[win]) || [];
  const maxViews = rows.reduce((m, r) => Math.max(m, r.views), 0) || 1;

  // Top modules (by 30-day views) become the chart's lines; pivot by_day → one row per date.
  const { chartData, topModules } = useMemo(() => {
    const byDay = (data && data.by_day) || [];
    if (!byDay.length) return { chartData: [], topModules: [] };
    const totals = {};
    byDay.forEach((x) => { totals[x.module] = (totals[x.module] || 0) + x.views; });
    const top = Object.keys(totals).sort((a, b) => totals[b] - totals[a]).slice(0, 5);
    const byDate = {};
    byDay.forEach((x) => {
      if (!top.includes(x.module)) return;
      byDate[x.date] = byDate[x.date] || { date: x.date };
      if (x.avg_ms != null) byDate[x.date][x.module] = +(x.avg_ms / 1000).toFixed(2);
    });
    const cd = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    return { chartData: cd, topModules: top };
  }, [data]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60]" data-testid="usage-stats-panel">
      <div className="absolute inset-0" style={{ background: "rgba(20,20,19,.32)" }} onClick={onClose} data-testid="usage-stats-backdrop" />
      <div className="absolute top-0 right-0 h-full flex flex-col"
        style={{ width: "min(560px, 100vw)", background: "#fff", borderLeft: `1px solid ${HAIR}`, boxShadow: "-18px 0 48px -24px rgba(0,0,0,.35)" }}>
        {/* header */}
        <div className="flex items-center" style={{ gap: 10, padding: "18px 20px", borderBottom: `1px solid ${HAIR}` }}>
          <span className="inline-flex items-center justify-center flex-none" style={{ width: 30, height: 30, borderRadius: 8, background: "#EEEEFB" }}>
            <BarChart3 size={17} style={{ color: IND }} />
          </span>
          <div className="min-w-0">
            <div style={{ fontSize: 15, fontWeight: 700, color: INK }}>Usage Indicators &amp; Support Debug Information</div>
            <div style={{ fontSize: 11.5, color: G9 }}>Pages, load times & API usage for this tenant</div>
          </div>
          <button type="button" onClick={onClose} data-testid="usage-stats-close"
            className="ml-auto inline-flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 8, color: G6 }}>
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto" style={{ padding: "18px 20px 40px", background: PANEL }}>
          {status === "loading" ? (
            <div className="flex items-center justify-center" style={{ height: 240, gap: 8, color: G9, fontSize: 13 }}>
              <Loader2 size={16} className="animate-spin" /> Reading traffic…
            </div>
          ) : status === "error" ? (
            <div style={{ fontSize: 13, color: "#B4231B", padding: 20 }}>Couldn't load usage stats.</div>
          ) : !data?.scoped ? (
            <div style={{ fontSize: 13, color: G6, padding: 20 }}>Sign in to see this tenant's usage.</div>
          ) : (
            <>
              {/* window tabs */}
              <div className="flex" style={{ background: "#EDEDEB", borderRadius: 10, padding: 3, gap: 2, width: "fit-content" }} data-testid="usage-win-tabs">
                {WINS.map(([k, lbl]) => {
                  const on = win === k;
                  return (
                    <button key={k} type="button" onClick={() => setWin(k)} data-testid={`usage-win-${k}`}
                      style={{ height: 30, padding: "0 12px", fontSize: 12.5, borderRadius: 8, fontWeight: on ? 600 : 500, color: on ? INK : G6, background: on ? "#fff" : "transparent", boxShadow: on ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>
                      {lbl}
                    </button>
                  );
                })}
              </div>

              {/* most used pages */}
              <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, marginTop: 14, padding: "16px 16px 10px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 2 }}>Most used pages</div>
                <div style={{ fontSize: 11.5, color: G9, marginBottom: 12 }}>Page opens · {WINS.find((w) => w[0] === win)[1].toLowerCase()}</div>
                {rows.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: G9, padding: "14px 0" }}>No activity in this window.</div>
                ) : rows.map((r) => (
                  <div key={r.module} className="flex items-center" style={{ gap: 12, padding: "7px 0" }} data-testid={`usage-bar-${r.module}`}>
                    <span style={{ width: 118, flex: "none", fontSize: 12.5, color: INK, fontWeight: 500 }} className="truncate">{label(r.module)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="block" style={{ height: 10, borderRadius: 6, background: IND, width: `${Math.max(4, (r.views / maxViews) * 100)}%`, transition: "width .4s ease" }} />
                    </span>
                    <span style={{ width: 118, flex: "none", textAlign: "right", fontSize: 11.5, color: G6, fontVariantNumeric: "tabular-nums" }}>
                      <b style={{ color: INK, fontWeight: 700 }}>{r.views}</b> opens
                      {r.avg_ms != null ? <span style={{ color: G9 }}> · {fmtMs(r.avg_ms)}</span> : null}
                      {r.searches > 0 ? <span style={{ color: G9 }}> · {r.searches} search{r.searches === 1 ? "" : "es"}</span> : null}
                    </span>
                  </div>
                ))}
              </div>

              {/* load time by module over days */}
              <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, marginTop: 14, padding: "16px 10px 12px 10px" }} data-testid="usage-loadchart">
                <div className="flex items-center" style={{ gap: 7, padding: "0 6px", marginBottom: 2 }}>
                  <Clock size={14} style={{ color: G6 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>Load time by module</span>
                </div>
                <div style={{ fontSize: 11.5, color: G9, padding: "0 6px", marginBottom: 10 }}>Average time to records, in seconds · last 30 days · top 5 pages</div>
                {chartData.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: G9, padding: "20px 6px" }}>Not enough timed loads yet.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={chartData} margin={{ top: 6, right: 12, left: -16, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EE" vertical={false} />
                      <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} />
                      <YAxis tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} width={42} unit="s" />
                      <Tooltip
                        formatter={(v, n) => [`${v}s`, label(n)]}
                        labelFormatter={(d) => d}
                        contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${HAIR}` }}
                      />
                      <Legend formatter={(v) => <span style={{ fontSize: 11, color: G6 }}>{label(v)}</span>} iconSize={9} />
                      {topModules.map((m, i) => (
                        <Line key={m} type="monotone" dataKey={m} stroke={LINE_COLORS[i % LINE_COLORS.length]}
                          strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* API usage — count + timing only, no endpoint names (support debug) */}
              <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, marginTop: 14, padding: "16px 10px 12px 10px" }} data-testid="usage-apichart">
                <div className="flex items-center" style={{ gap: 7, padding: "0 6px", marginBottom: 2 }}>
                  <Server size={14} style={{ color: G6 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>API usage</span>
                </div>
                <div style={{ fontSize: 11.5, color: G9, padding: "0 6px", marginBottom: 12 }}>Calls made to the service and how long they took · {WINS.find((w) => w[0] === win)[1].toLowerCase()}</div>
                {(() => {
                  const a = (data.api && data.api[win]) || { calls: 0, avg_ms: 0, total_ms: 0, errors: 0 };
                  const tiles = [["Total calls", String(a.calls), null, false], ["Avg response", fmtMs(a.avg_ms), null, false], ["Time taken", fmtDur(a.total_ms), a.errors ? `${a.errors} failed` : null, !!a.errors]];
                  return (
                    <>
                    <div className="grid grid-cols-3 gap-2" style={{ padding: "0 6px" }} data-testid="usage-api-tiles">
                      {tiles.map(([k, v, sub, clickable]) => (
                        <div key={k} style={{ background: PANEL, borderRadius: 10, padding: "10px 12px" }}>
                          <div style={{ fontSize: 10.5, color: G9, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{k}</div>
                          <div style={{ fontSize: 18, fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{v}</div>
                          {sub ? (clickable ? (
                            <button type="button" onClick={toggleErrors} data-testid="usage-api-failed-toggle"
                              className="inline-flex items-center" style={{ gap: 3, fontSize: 10.5, color: "#C2410C", marginTop: 1, fontWeight: 600, cursor: "pointer" }}>
                              {sub} <ChevronDown size={11} style={{ transform: errOpen ? "rotate(180deg)" : "none", transition: "transform .2s ease" }} />
                            </button>
                          ) : <div style={{ fontSize: 10.5, color: "#C2410C", marginTop: 1 }}>{sub}</div>) : null}
                        </div>
                      ))}
                    </div>
                    {a.errors && errOpen ? (
                      <div style={{ margin: "10px 6px 0", border: "1px solid #F0D9CF", borderRadius: 10, background: "#FFF7F3", overflow: "hidden" }} data-testid="usage-api-failed-list">
                        <div className="flex items-center" style={{ gap: 6, padding: "9px 12px", borderBottom: "1px solid #F3E2D9" }}>
                          <AlertTriangle size={13} style={{ color: "#C2410C" }} />
                          <span style={{ fontSize: 11.5, fontWeight: 700, color: "#9A3412" }}>Failed API calls</span>
                          <span style={{ fontSize: 10.5, color: G9 }}>· last 30 days</span>
                        </div>
                        {errStatus === "loading" ? (
                          <div className="flex items-center" style={{ gap: 7, padding: "14px 12px", fontSize: 12, color: G6 }}><Loader2 size={14} className="animate-spin" /> Loading details…</div>
                        ) : errStatus === "error" ? (
                          <div style={{ padding: "14px 12px", fontSize: 12, color: "#B4231B" }}>Couldn't load error details.</div>
                        ) : !(errData?.errors || []).length ? (
                          <div style={{ padding: "14px 12px", fontSize: 12, color: G6 }}>No failure details recorded in the last 30 days.</div>
                        ) : (
                          <div style={{ maxHeight: 280, overflowY: "auto" }}>
                            {errData.errors.map((e, i) => (
                              <div key={i} data-testid={`usage-api-failed-row-${i}`}
                                style={{ padding: "9px 12px", borderTop: i ? "1px solid #F5E7DF" : "none" }}>
                                <div className="flex items-center" style={{ gap: 7, flexWrap: "wrap" }}>
                                  <span style={{ fontSize: 10, fontWeight: 800, color: "#9A3412", background: "#FBE4D8", borderRadius: 5, padding: "1px 6px", letterSpacing: ".03em" }}>{statusLabel(e.status)}</span>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: INK, fontVariantNumeric: "tabular-nums" }}>{e.method}</span>
                                  <span style={{ fontSize: 11, color: G6, fontVariantNumeric: "tabular-nums", marginLeft: "auto" }}>{fmtTs(e.ts)}</span>
                                </div>
                                <div style={{ fontSize: 11, color: G6, marginTop: 3, wordBreak: "break-all" }}>{e.path}{e.query ? `?${e.query}` : ""}</div>
                                {e.detail ? <div style={{ fontSize: 11, color: "#9A3412", marginTop: 3, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{e.detail.length > 300 ? `${e.detail.slice(0, 300)}…` : e.detail}</div> : null}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                    </>
                  );
                })()}
                {(data.api?.by_day || []).length === 0 ? (
                  <div style={{ fontSize: 12.5, color: G9, padding: "16px 6px 4px" }}>No API calls recorded yet.</div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={data.api.by_day} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EE" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} />
                        <YAxis yAxisId="c" tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} width={34} allowDecimals={false} />
                        <YAxis yAxisId="ms" orientation="right" tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} width={46} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}s` : `${v}ms`)} />
                        <Tooltip formatter={(v, n) => (n === "calls" ? [v, "Calls"] : [fmtMs(v), "Avg response"])} labelFormatter={(d) => d} contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${HAIR}` }} />
                        <Legend formatter={(v) => <span style={{ fontSize: 11, color: G6 }}>{v === "calls" ? "Calls" : "Avg response"}</span>} iconSize={9} />
                        <Line yAxisId="c" type="monotone" dataKey="calls" stroke="#5B5BD6" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} isAnimationActive={false} name="calls" />
                        <Line yAxisId="ms" type="monotone" dataKey="avg_ms" stroke="#D97706" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} isAnimationActive={false} name="avg_ms" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              {/* Chart loads — open time + ETag instant re-opens (support debug) */}
              <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 12, marginTop: 14, padding: "16px 10px 12px 10px" }} data-testid="usage-chartloads">
                <div className="flex items-center" style={{ gap: 7, padding: "0 6px", marginBottom: 2 }}>
                  <FileClock size={14} style={{ color: G6 }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: INK }}>Chart loads</span>
                </div>
                <div style={{ fontSize: 11.5, color: G9, padding: "0 6px", marginBottom: 12 }}>How many charts were opened, how long fresh opens took, and how many re-opened instantly from the ETag cache · {WINS.find((w) => w[0] === win)[1].toLowerCase()}</div>
                {(() => {
                  const c = (data.charts && data.charts[win]) || { opens: 0, avg_ms: 0, cached: 0, fresh: 0 };
                  const rate = c.opens ? Math.round((c.cached / c.opens) * 100) : 0;
                  const tiles = [
                    ["Charts opened", String(c.opens), null],
                    ["Avg open time", fmtMs(c.avg_ms), c.fresh ? `${c.fresh} fresh load${c.fresh === 1 ? "" : "s"}` : null],
                    ["Instant re-opens", String(c.cached), c.opens ? `${rate}% from cache` : null],
                  ];
                  return (
                    <>
                      <div className="grid grid-cols-3 gap-2" style={{ padding: "0 6px" }} data-testid="usage-chart-tiles">
                        {tiles.map(([k, v, sub], i) => (
                          <div key={k} style={{ background: PANEL, borderRadius: 10, padding: "10px 12px" }}>
                            <div className="flex items-center" style={{ gap: 4 }}>
                              {i === 2 ? <Zap size={11} style={{ color: "#1E7F4F" }} /> : null}
                              <span style={{ fontSize: 10.5, color: G9, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em" }}>{k}</span>
                            </div>
                            <div style={{ fontSize: 18, fontWeight: 700, color: i === 2 ? "#1E7F4F" : INK, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{v}</div>
                            {sub ? <div style={{ fontSize: 10.5, color: G9, marginTop: 1 }}>{sub}</div> : null}
                          </div>
                        ))}
                      </div>
                      {/* cache hit-rate bar — the separate "did it use the ETag cache" component */}
                      {c.opens ? (
                        <div style={{ padding: "12px 6px 0" }} data-testid="usage-chart-cachebar">
                          <div className="flex items-center justify-between" style={{ fontSize: 11, color: G6, marginBottom: 5 }}>
                            <span>Cache hit rate</span>
                            <span style={{ fontVariantNumeric: "tabular-nums" }}><b style={{ color: "#1E7F4F" }}>{c.cached} cached</b> · {c.fresh} fresh</span>
                          </div>
                          <span className="block" style={{ height: 10, borderRadius: 6, background: "#E6EEE9", overflow: "hidden" }}>
                            <span className="block" style={{ height: "100%", width: `${rate}%`, background: "#1E7F4F", transition: "width .4s ease" }} />
                          </span>
                        </div>
                      ) : null}
                    </>
                  );
                })()}
                {(data.charts?.by_day || []).length === 0 ? (
                  <div style={{ fontSize: 12.5, color: G9, padding: "16px 6px 4px" }}>No chart opens recorded yet.</div>
                ) : (
                  <div style={{ marginTop: 12 }}>
                    <ResponsiveContainer width="100%" height={200}>
                      <LineChart data={data.charts.by_day} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F0F0EE" vertical={false} />
                        <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} />
                        <YAxis yAxisId="c" tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} width={34} allowDecimals={false} />
                        <YAxis yAxisId="ms" orientation="right" tick={{ fontSize: 11, fill: G9 }} stroke={HAIR} width={46} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}s` : `${v}ms`)} />
                        <Tooltip formatter={(v, n) => (n === "avg_ms" ? [fmtMs(v), "Avg open time"] : [v, n === "fresh" ? "Fresh loads" : "Instant re-opens"])} labelFormatter={(d) => d} contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${HAIR}` }} />
                        <Legend formatter={(v) => <span style={{ fontSize: 11, color: G6 }}>{v === "avg_ms" ? "Avg open time" : v === "fresh" ? "Fresh loads" : "Instant re-opens"}</span>} iconSize={9} />
                        <Line yAxisId="c" type="monotone" dataKey="fresh" stroke="#5B5BD6" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} isAnimationActive={false} name="fresh" />
                        <Line yAxisId="c" type="monotone" dataKey="cached" stroke="#1E7F4F" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 4 }} isAnimationActive={false} name="cached" />
                        <Line yAxisId="ms" type="monotone" dataKey="avg_ms" stroke="#D97706" strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} name="avg_ms" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>

              <div style={{ fontSize: 11, color: G9, marginTop: 12, lineHeight: 1.5 }}>
                Private to this tenant · no patient data and no endpoint names are recorded. Figures reflect in-app
                page opens, the time until each page's records appeared, and the volume and response time of service calls.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
